import React, { useContext, useEffect, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, GridLegacy as Grid } from "@mui/material";
import { api, AssetLocation, IListCarParks, RequestStatus, Terminal } from "../../../logic/api";
import propTypes from "prop-types";
import NotchedCard from "../../../components/cards/NotchedCard/NotchedCard";
import EditableField, { EditableFieldTypes } from "../../../components/editable/EditableField/EditableField";
import EditableGroup, { EditableGroupType } from "../../../components/editable/EditableGroup/EditableGroup";
import { SessionContext } from "../../../utility/useSession";
import ErrorDialog from "../../../components/notifications/errorDialog/ErrorDialog";
import { ProgressView } from "../../../components/notifications/progress/ProgressView";
import { PagedDataView } from "../../../components/lists/PagedDataView/PagedDataView";
import flex from "../../../styles/flex";
import { useTranslation } from "react-i18next";

const TerminalEditorDialog: React.FunctionComponent<{
  customerId: number;
  terminal?: Terminal | null;
  show: boolean;
  onClose: (didSave: boolean, terminal: Terminal | null) => void;
}> = (props) => {
  const session = useContext(SessionContext);
  const { t } = useTranslation();

  const [carParkListFetchParams, setCarParkListFetchParams] = useState<IListCarParks>({
    customerId: props.customerId
  });

  useEffect(() => {
    setCarParkListFetchParams({ ...carParkListFetchParams, customerId: props.customerId });
  }, [props.customerId]);

  const [terminal, setTerminal] = useState<Terminal>(props.terminal || {
    active: true,
    code: "",
    name: ""
  } as Terminal);

  const [carParkIds, setCarParkIds] = useState<number[]>([]);


  const saveTerminal = api.hooks.admin.saveTerminal();

  const isLoading =
    saveTerminal.status == RequestStatus.loading;


  useEffect(() => {
    if (props.terminal == null) {
      setTerminal({
        active: true,
        code: "",
        name: ""
      } as Terminal);
      setCarParkIds([]);
    } else {
      setTerminal(props.terminal);
      setCarParkIds(props.terminal.carParks);
    }

  }, [props.terminal]);


  return <Dialog
    maxWidth={isLoading == true ? undefined : "sm"}
    fullWidth={isLoading == true ? false : true}
    open={props.show}
    keepMounted
    onClose={() => props.onClose(false, null)}
    aria-labelledby="alert-dialog-slide-title"
    aria-describedby="alert-dialog-slide-description"
  >
    {isLoading == false && (
      <DialogTitle id="alert-dialog-slide-title">
        <div style={{ textAlign: "center" }}>
          {t("flightLookup.labels.terminal")}
        </div>
      </DialogTitle>
    )}

    <DialogContent>
      <ErrorDialog err={saveTerminal.err}></ErrorDialog>
      <ProgressView
        showError={false}
        noDelay={true}
        status={saveTerminal.status}
      >
        <div style={{ minHeight: 500 }}>

          <NotchedCard label={t("flightLookup.labels.terminal")}>
            <EditableGroup<Terminal>
              id="terminal"
              testId="terminal-editor-group"
              editType={EditableGroupType.alwaysEditing}
              data={terminal}
              onDataChanged={async (data) => {
                setTerminal(data);
                return true;
              }}
            >
              <Grid container>


                <EditableField<Terminal, string>
                  inputType={EditableFieldTypes.text}
                  label={t("admin.terminalName")}
                  onProvide={(data) => data.name}
                  onChange={(data, value) => data.name = value}
                />


                <EditableField<Terminal, string>
                  inputType={EditableFieldTypes.text}
                  label={t("admin.terminalCode")}
                  onProvide={(data) => data.code}
                  onChange={(data, value) => data.code = value}
                />


                <EditableField<Terminal, boolean>
                  inputType={EditableFieldTypes.switch}
                  label={t("mobile.active")}
                  onProvide={(data) => data.active}
                  onChange={(data, value) => data.active = value}
                />
              </Grid>
            </EditableGroup>
          </NotchedCard>
          <NotchedCard label={t("admin.carparks")}>
            <PagedDataView<IListCarParks, AssetLocation>
              apiHook={api.hooks.admin.listCarParks}
              parameters={carParkListFetchParams}
              renderItem={(item) => {
                return (
                  <div style={{ padding: 20 }}>
                    <div style={flex.flexRows}>
                      <div style={flex.flexAuto}>
                        <input style={{ zoom: 1.5 }} type="checkbox" checked={carParkIds.indexOf(item.id) != -1} onChange={() => {
                          const ids = [...carParkIds];
                          const indx = ids.indexOf(item.id);

                          if (indx != -1) {
                            ids.splice(indx, 1);
                          } else {
                            ids.push(item.id);
                          }

                          setCarParkIds(ids);
                        }} />
                      </div>
                      <div style={flex.flexFill}>
                        <div style={{ marginLeft: 10, marginTop: 2 }}> <b>{item.name}</b></div>
                      </div>
                    </div>
                  </div>
                );
              }}
            >
              <div style={{ padding: 20, backgroundColor: "#eee" }}>
                {t("admin.noCustomers")}
              </div>
            </PagedDataView>
          </NotchedCard>
        </div>
      </ProgressView>
    </DialogContent>
    {isLoading == false && (
      <DialogActions>
        <Button
          onClick={() => {
            props.onClose(false, null);
          }}
          color="primary"
        >
          {t("general.close")}
        </Button>
        <Button
          onClick={() => {
            saveTerminal.send(session.current.context, {
              customerId: props.customerId,
              terminal: terminal,
              carParks: carParkIds
            }, (result) => {
              if (result != null) {
                props.onClose(true, null);
              }
            });
            console.log("x");
          }}
          color="primary"
        >
          {t("general.save")}
        </Button>
      </DialogActions>
    )}

  </Dialog>;
}

TerminalEditorDialog.propTypes = {
  customerId: propTypes.number.isRequired,
  terminal: propTypes.any,
  show: propTypes.bool.isRequired,
  onClose: propTypes.func.isRequired,
};

export default TerminalEditorDialog;
